// The tax calculation frontage and Core have to agree on. Core's side is
// modules/payments/app/components/payments/taxes/tax_util.rb and
// app/models/concerns/itemizable_item_totals.rb. Core is the source of truth for what a rate owes
// on a base; a cent that moves here must move there. Totalling a tax across lines before rounding
// is new and has no Core counterpart yet.
//
// Other copies of a tax calculation in frontage, deliberately left on per-line rounding:
// pov/src/modules/payments/utils/taxUtil.js (carts and POS),
// vue/src/modules/settings/utils/taxesUtil.js and
// vue/src/modules/products/components/TaxSummary/TaxSummary.vue (catalog previews),
// app/assets/frontage/app/crm/components/tax-summary/tax-summary.js.coffee (legacy previews).
// vue/src/modules/itemizable/utils/taxCalculator.js is a copy of this file - keep the two in sync.
//
// Amounts are held as integers, money in cents. An exact tax - one that is not yet rounded to
// the cent - is held to seven decimal places, so a half cent is exactly 50000 and never
// 49999.9: the rule turns on half cents, and 40.50 * 0.03 is 1.2149999999999999 in a double.

const INCLUDE_TAX_MODE = 'include';
const EXACT_DECIMALS = 7;
const EXACT_PER_UNIT = 10 ** EXACT_DECIMALS; // 1.00 of the currency
const EXACT_PER_CENT = EXACT_PER_UNIT / 100;

export const TAX_STRATEGIES = {
  grouped: 'grouped',
  perLine: 'per_line',
};

const toDecimal = (value) => Math.round((parseFloat(value) || 0) * EXACT_PER_UNIT);

const toMoney = (cents) => cents / 100;

const sumAmounts = (amounts) => amounts.reduce((total, amount) => total + amount, 0);

// BigDecimal::ROUND_HALF_UP - a half cent goes away from zero. Rounding the magnitude is what
// makes that true of negatives too; Math.round on its own would send -2.475 to -2.47.
const roundMoney = (exact) => (exact < 0 ? -1 : 1) * Math.round(Math.abs(exact) / EXACT_PER_CENT);

// Half up, away from zero, to the cent - BigDecimal::ROUND_HALF_UP applied to a currency amount.
// Exported for the previews that round a single price and never allocate across lines.
export const roundToCents = (value) => toMoney(roundMoney(toDecimal(value)));

// Splits a total that was rounded once back over the exact amounts it was summed from.
// Largest remainder, ties by position - so the same input always gives the same output.
const allocateByRemainder = (exactAmounts, totalCents) => {
  // Truncation towards zero, so a credit note behaves like its positive mirror.
  const wholeCents = exactAmounts.map((amount) => Math.trunc(amount / EXACT_PER_CENT));
  const leftover = totalCents - sumAmounts(wholeCents);
  if (!exactAmounts.length || leftover === 0) return wholeCents;

  const direction = leftover < 0 ? -1 : 1;
  const remainderOf = (index) => exactAmounts[index] - wholeCents[index] * EXACT_PER_CENT;
  const byRemainder = exactAmounts
    .map((amount, index) => index)
    .sort((a, b) => (remainderOf(b) - remainderOf(a)) * direction || a - b);

  // Cycling keeps the sum exact even for a total further away than one cent per amount.
  const allocated = [...wholeCents];
  for (let taken = 0; taken < Math.abs(leftover); taken += 1) {
    allocated[byRemainder[taken % byRemainder.length]] += direction;
  }
  return allocated;
};

// An include-mode gross is read at the cent it is stored with; an exclude-mode net stays exact,
// so a 12.345 discount is taxed on 87.655 and not on the 87.66 the line shows.
const calcTaxBase = (line, includeMode) => {
  const base = toDecimal(line.base);
  return includeMode ? roundMoney(base) * EXACT_PER_CENT : base;
};

const calcExactTax = (base, rate, totalRate, includeMode) => {
  if (!includeMode) return Math.round((base * rate) / 100);

  const divisor = 100 + totalRate;
  return divisor === 0 ? 0 : Math.round((base * rate) / divisor);
};

const getTotalRate = (taxes) => sumAmounts(taxes.map((tax) => parseFloat(tax.rate) || 0));

// One entry per line tax, in line order and then in the order the taxes sit on the line -
// the order cents are handed out in.
const buildLineTaxes = (lines, bases, includeMode) => lines.flatMap((line, lineIndex) => {
  const taxes = line.taxes || [];
  // The taxes on a line share its gross: gross * rate / (100 + the line's total rate).
  const totalRate = includeMode ? getTotalRate(taxes) : 0;

  return taxes.map((tax) => {
    const rate = parseFloat(tax.rate) || 0;
    return {
      lineIndex,
      description: tax.description || tax.name,
      rate,
      // Per tax, not per line: a line can carry a settled tax beside one still to compute.
      storedTotal: line.fixed_taxes && tax.tax_total != null
        ? roundMoney(toDecimal(tax.tax_total))
        : null,
      exactTotal: calcExactTax(bases[lineIndex], rate, totalRate, includeMode),
    };
  });
}).map((lineTax, index) => ({ ...lineTax, index }));

// \0 cannot appear in a tax description, which a merchant types.
const taxKey = (lineTax) => `${lineTax.description}\0${lineTax.rate}`;

const groupByTaxKey = (lineTaxes) => {
  const groups = new Map();
  lineTaxes.forEach((lineTax) => {
    const key = taxKey(lineTax);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(lineTax);
  });
  return [...groups.values()];
};

// The group's total rounded once, then split back over the lines it was summed from.
const calcGroupTotals = (lineTaxes) => {
  const exactTotals = lineTaxes.map((lineTax) => lineTax.exactTotal);
  return allocateByRemainder(exactTotals, roundMoney(sumAmounts(exactTotals)));
};

// The tax each line tax owes, in cents, by its position in lineTaxes. A stored total is honoured
// as it stands and only joins the group total.
const calcTaxTotals = (lineTaxes, grouped) => {
  const totals = [];
  groupByTaxKey(lineTaxes).forEach((group) => {
    const stored = group.filter((lineTax) => lineTax.storedTotal !== null);
    const toRound = group.filter((lineTax) => lineTax.storedTotal === null);
    stored.forEach((lineTax) => { totals[lineTax.index] = lineTax.storedTotal; });

    if (grouped) {
      const allocated = calcGroupTotals(toRound);
      toRound.forEach((lineTax, index) => { totals[lineTax.index] = allocated[index]; });
    } else {
      toRound.forEach((lineTax) => { totals[lineTax.index] = roundMoney(lineTax.exactTotal); });
    }
  });
  return totals;
};

const getTaxTotal = (lineTax, taxTotalCents) => ({
  description: lineTax.description,
  rate: lineTax.rate,
  tax_total: toMoney(taxTotalCents),
});

const calcLineTotals = (lineTaxes, totals, base, includeMode) => {
  const taxTotal = sumAmounts(lineTaxes.map((lineTax) => totals[lineTax.index]));
  // In include mode the gross does not move, so net + tax == gross holds by construction.
  const netTotal = includeMode ? roundMoney(base) - taxTotal : roundMoney(base);
  const grandTotal = includeMode ? roundMoney(base) : netTotal + taxTotal;

  return {
    taxes: lineTaxes.map((lineTax) => getTaxTotal(lineTax, totals[lineTax.index])),
    taxTotal,
    netTotal,
    grandTotal,
  };
};

const getGroupTotals = (lineTaxes, totals) => groupByTaxKey(lineTaxes).map(
  (group) => getTaxTotal(group[0], sumAmounts(group.map((lineTax) => totals[lineTax.index]))),
);

/**
 * One tax calculation for a whole document.
 *
 * lines - [{ base, taxes: [{ description|name, rate, tax_total }], fixed_taxes }]. base is the
 * line's net in exclude mode and its gross in include mode. fixed_taxes marks a line whose
 * tax_totals were settled elsewhere: they are honoured and only join the group total.
 *
 * strategy - 'grouped' totals each tax group over every line it applies to and rounds once;
 * 'per_line' rounds every line's tax on its own, so no cent moves between lines.
 */
export const calcDocumentTaxes = ({ lines = [], taxMode, strategy = TAX_STRATEGIES.perLine }) => {
  const includeMode = taxMode === INCLUDE_TAX_MODE;
  const bases = lines.map((line) => calcTaxBase(line, includeMode));
  const lineTaxes = buildLineTaxes(lines, bases, includeMode);
  const totals = calcTaxTotals(lineTaxes, strategy === TAX_STRATEGIES.grouped);

  const lineTotals = lines.map((line, index) => {
    const ofLine = lineTaxes.filter((lineTax) => lineTax.lineIndex === index);
    return calcLineTotals(ofLine, totals, bases[index], includeMode);
  });

  return {
    lines: lineTotals.map((line) => ({
      taxes: line.taxes,
      tax_total: toMoney(line.taxTotal),
      net_total: toMoney(line.netTotal),
      grand_total: toMoney(line.grandTotal),
    })),
    taxes: getGroupTotals(lineTaxes, totals),
    tax_total: toMoney(sumAmounts(lineTotals.map((line) => line.taxTotal))),
    net_total: toMoney(sumAmounts(lineTotals.map((line) => line.netTotal))),
    grand_total: toMoney(sumAmounts(lineTotals.map((line) => line.grandTotal))),
  };
};

export default { calcDocumentTaxes, roundToCents, TAX_STRATEGIES };
